import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { list, create, update, remove } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { ExportButtons } from "@/components/ExportButtons";
import { Field } from "@/components/FormField";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

// Reusable table + CRUD dialog (no page chrome). Use inside a Layout.
export const EntityTable = ({ collection, columns, fields, defaults, filter, title, idKey }) => {
  const key = idKey || collection;
  const singular = (title || collection).replace(/s$/, "");
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: [collection], queryFn: () => list(collection) });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [delId, setDelId] = useState(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: [collection] });
  const saveMut = useMutation({
    mutationFn: (body) => (editing ? update(collection, editing.id, body) : create(collection, body)),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? "Updated" : "Created"); },
    onError: () => toast.error("Save failed"),
  });
  const delMut = useMutation({
    mutationFn: (id) => remove(collection, id),
    onSuccess: () => { invalidate(); setDelId(null); toast.success("Deleted"); },
  });

  const openNew = () => { setEditing(null); setForm(defaults ? { ...defaults } : {}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm(row); setOpen(true); };
  const setField = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const source = filter ? data.filter(filter) : data;
  const filtered = source.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid={`search-${key}`} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search…" className="pl-9 h-9" />
        </div>
        <Button size="sm" data-testid={`add-${key}-btn`} onClick={openNew}
          className="gap-2 bg-[#0055FF] hover:bg-[#0044CC]">
          <Plus className="h-4 w-4" /> New {singular}
        </Button>
      </div>

      <div className="w-full overflow-auto rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              {columns.map((c) => (
                <TableHead key={c.key}
                  className={`text-xs uppercase tracking-[0.08em] font-semibold whitespace-nowrap ${c.align === "right" ? "text-right" : ""}`}>
                  {c.label}
                </TableHead>
              ))}
              <TableHead className="text-right w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-muted-foreground">No records. Click "New {singular}" to add one.</TableCell></TableRow>
            ) : filtered.map((row) => (
              <TableRow key={row.id} data-testid={`row-${key}-${row.id}`} className="hover:bg-muted/50">
                {columns.map((c) => (
                  <TableCell key={c.key}
                    className={`font-mono-data text-[13px] whitespace-nowrap ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                    {c.render ? c.render(row) : (row[c.key] ?? <span className="text-neutral-300">—</span>)}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      data-testid={`edit-${key}-${row.id}`} onClick={() => openEdit(row)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700"
                      data-testid={`delete-${key}-${row.id}`} onClick={() => setDelId(row.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${singular}` : `New ${singular}`}</DialogTitle>
            <DialogDescription>Fill in the details below and save.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <Field key={f.field} {...f} value={form[f.field]} onChange={setField} />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-form-btn">Cancel</Button>
            <Button data-testid="save-form-btn" className="bg-[#0055FF] hover:bg-[#0044CC]"
              disabled={saveMut.isPending} onClick={() => saveMut.mutate(form)}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700"
              onClick={() => delMut.mutate(delId)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const EntityManager = ({ collection, title, subtitle, columns, fields, defaults, filter }) => (
  <Layout>
    <PageHeader title={title} subtitle={subtitle}>
      <ExportButtons collection={collection} />
    </PageHeader>
    <div className="p-8 animate-fade-up">
      <EntityTable collection={collection} title={title} columns={columns} fields={fields} defaults={defaults} filter={filter} />
    </div>
  </Layout>
);

export { StatusPill };
